import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../store/uiSlice';
import { reportException } from '../services/exceptionService';

const EXCEPTION_TYPES = [
  {
    value: 'access_issue',
    label: 'Access Issue',
    emoji: '🔐',
    desc: "Can't enter facility or a specific area",
  },
  {
    value: 'patient_unavailable',
    label: 'Patient Problem',
    emoji: '👤',
    desc: 'Patient refused, unavailable, or not found',
  },
  {
    value: 'specimen_problem',
    label: 'Specimen Problem',
    emoji: '🧪',
    desc: 'Compromised, mislabeled, or missing sample',
  },
  {
    value: 'equipment',
    label: 'Equipment Problem',
    emoji: '🔧',
    desc: 'Supplies missing or malfunctioning',
  },
  {
    value: 'other',
    label: 'Other',
    emoji: '📋',
    desc: 'Any other issue at this stop',
  },
];

function ExceptionPanel({ stopId, facilityId, stopName, open, onClose }) {
  const dispatch = useDispatch();
  const [type, setType] = useState('access');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = description.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await reportException({ stopId, facilityId, type, desc: description.trim() });
      dispatch(showToast('Exception reported ✓'));
      setDescription('');
      setType('access');
      onClose();
    } catch (err) {
      console.error('Exception report failed', err);
      dispatch(showToast('Failed to submit — try again'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    /* z-index: 20 puts this above the detail-panel (z-index: 10) it sits inside */
    <div className={`detail-panel ${open ? 'open' : ''}`} style={{ zIndex: 20 }}>

      {/* ── Header ── */}
      <div className="detail-header" style={{ background: '#c62828' }}>
        <button className="back-btn" onClick={onClose}>← Back</button>
        <div>
          <div className="detail-title">Report Exception</div>
          <div className="detail-subtitle">{stopName}</div>
        </div>
      </div>

      <div className="detail-body">
        {/* ── Exception type ── */}
        <div className="card">
          <div
            className="card-label"
            style={{ color: '#c62828', marginBottom: 12 }}
          >
            Exception Type
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {EXCEPTION_TYPES.map((t) => {
              const selected = type === t.value;
              return (
                <label
                  key={t.value}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
                    border: `1.5px solid ${selected ? '#c62828' : 'var(--rgray)'}`,
                    background: selected ? '#FFEBEE' : '#fafafa',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name="exc-type"
                    value={t.value}
                    checked={selected}
                    onChange={() => setType(t.value)}
                    style={{ accentColor: '#c62828', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{t.emoji}</span>
                  <div>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: selected ? '#c62828' : '#1a1a1a',
                    }}>
                      {t.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--bodygray)', marginTop: 1 }}>
                      {t.desc}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* ── Description ── */}
        <div className="card">
          <div className="card-label" style={{ color: '#c62828', marginBottom: 8 }}>
            Description <span style={{ color: '#c62828' }}>*</span>
          </div>
          <textarea
            className="note-input"
            style={{ height: 110, borderColor: description.trim() ? 'var(--rgray)' : '#f0c0c0' }}
            placeholder="Describe the issue in detail so your supervisor can act on it..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {!description.trim() && (
            <div style={{ fontSize: 11, color: '#c62828', marginTop: 4 }}>
              Description is required
            </div>
          )}

          {/* ── Actions ── */}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button
              className="action-btn"
              style={{ flex: 1, marginBottom: 0 }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="action-btn"
              style={{
                flex: 2, marginBottom: 0,
                background: canSubmit ? '#c62828' : '#e9a0a0',
                color: 'white', border: 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                transition: 'background 0.15s',
              }}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? 'Submitting…' : 'Submit Report'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default ExceptionPanel;